import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  Form,
  FormControl,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyUser = {
  userId: "",
  storeId: "",
  username: "",
  email: "",
  passwordHash: "",
  fullName: "",
  roleId: 0,
  isActive: true,
};

export default class CrmUsers extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      users: [],
      filteredUsers: [],
      loading: true,
      search: "",
      error: null,
      showModal: false,
      isEdit: false,
      currentUser: { ...emptyUser },
    };
  }

  componentDidMount() {
    this.loadUsers();
  }

  loadUsers = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const users = await fetchCrmList("Users", "GetUsers", storeId);
      const normalized = Array.isArray(users) ? users : [];
      this.setState({
        users: normalized,
        filteredUsers: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load users.",
      });
    }
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredUsers = this.state.users.filter((user) => {
      const username = (user.username || "").toLowerCase();
      const email = (user.email || "").toLowerCase();
      const fullName = (user.fullName || "").toLowerCase();
      return (
        username.includes(normalizedSearch) ||
        email.includes(normalizedSearch) ||
        fullName.includes(normalizedSearch)
      );
    });
    this.setState({ search, filteredUsers });
  };

  handleShowModal = (user = null) => {
    this.setState({
      currentUser: user
        ? { ...user, passwordHash: user.passwordHash || "" }
        : { ...emptyUser },
      isEdit: Boolean(user),
      showModal: true,
      error: null,
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentUser: {
        ...prevState.currentUser,
        [field]: value,
      },
    }));
  };

  saveUser = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const user = {
      ...this.state.currentUser,
      storeId,
      userId:
        this.state.currentUser.userId || "00000000-0000-0000-0000-000000000000",
      roleId: Number(this.state.currentUser.roleId) || 0,
      passwordHash: this.state.currentUser.passwordHash || "password",
    };

    if (!user.username) {
      this.setState({ error: "Username is required." });
      return;
    }

    try {
      await saveCrmItem("Users", "AddOrUpdateUser", user);
      await this.loadUsers();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save user." });
    }
  };

  deleteUser = async (userId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;
    if (!window.confirm("Delete this user?")) return;

    try {
      await deleteCrmItem("Users", "DeleteUser", storeId, userId);
      await this.loadUsers();
    } catch (error) {
      this.setState({ error: error.message || "Unable to delete user." });
    }
  };

  render() {
    const {
      filteredUsers,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentUser,
    } = this.state;
    const storeId = this.context.storeId;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Users</h2>
            <p className="text-muted">
              Manage CRM users for the selected store.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add User
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadUsers}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM users.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3">
          <Col md={6} lg={4}>
            <FormControl
              placeholder="Search by username, email, or name"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>RoleId</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-5">
                  No users found.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.userId || Math.random()}>
                  <td>{user.username || "-"}</td>
                  <td>{user.fullName || "-"}</td>
                  <td>{user.email || "-"}</td>
                  <td>{user.roleId || "-"}</td>
                  <td>{user.isActive ? "Active" : "Inactive"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(user)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deleteUser(user.userId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>{isEdit ? "Edit User" : "Add User"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3" controlId="username">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  value={currentUser.username}
                  onChange={(e) =>
                    this.handleFieldChange("username", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="fullName">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  value={currentUser.fullName}
                  onChange={(e) =>
                    this.handleFieldChange("fullName", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={currentUser.email}
                  onChange={(e) =>
                    this.handleFieldChange("email", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="roleId">
                <Form.Label>RoleId</Form.Label>
                <Form.Control
                  type="number"
                  value={currentUser.roleId}
                  onChange={(e) =>
                    this.handleFieldChange("roleId", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="isActive">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={currentUser.isActive || false}
                  onChange={(e) =>
                    this.handleFieldChange("isActive", e.target.checked)
                  }
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveUser}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
